import CheckIcon from "@mui/icons-material/CheckRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import LinkIcon from "@mui/icons-material/InsertLinkRounded";
import PlayArrowIcon from "@mui/icons-material/PlayArrowRounded";
import { Box, CardContent, CardMedia, Fab, IconButton, Tooltip, Typography } from "@mui/material";
import Card from "@mui/material/Card";
import { useState } from "react";
import { useNavigate } from "react-router";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import InlineTags from "@entrilia/shared/components/InlineTags";
import TypographyMultilineEllipsis from "@entrilia/shared/components/TypographyMultilineEllipsis";
import { getCategoryColorFromNameHash } from "@entrilia/shared/utilities/categoryColor";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { formatDurationInSeconds } from "@entrilia/shared/utilities/formatters";
import { isDefined } from "@entrilia/shared/utilities/typeHelper";
import type { VideoLibraryCategory, VideoLibraryItem } from "../../../api/types/videoLibraryTypes";
import useClientContext from "../../../context/useClientContext";
import ThumbnailImage01 from "../../../images/video_thumb_01.png";
import ThumbnailImage02 from "../../../images/video_thumb_02.png";
import ThumbnailImage03 from "../../../images/video_thumb_03.png";
import ThumbnailImage04 from "../../../images/video_thumb_04.png";
import ThumbnailImage05 from "../../../images/video_thumb_05.png";
import ThumbnailImage06 from "../../../images/video_thumb_06.png";
import ThumbnailImage07 from "../../../images/video_thumb_07.png";
import ThumbnailImage08 from "../../../images/video_thumb_08.png";
import ThumbnailImage09 from "../../../images/video_thumb_09.png";
import ThumbnailImage10 from "../../../images/video_thumb_10.png";
import ThumbnailImage11 from "../../../images/video_thumb_11.png";
import ThumbnailImage12 from "../../../images/video_thumb_12.png";
import { pageRoutes } from "../../../routes";

const thumbnailImages = [
  ThumbnailImage01,
  ThumbnailImage02,
  ThumbnailImage03,
  ThumbnailImage04,
  ThumbnailImage05,
  ThumbnailImage06,
  ThumbnailImage07,
  ThumbnailImage08,
  ThumbnailImage09,
  ThumbnailImage10,
  ThumbnailImage11,
  ThumbnailImage12,
];

interface Props {
  videoInfo: VideoLibraryItem;
  thumbnailIndex: number;
  allCategories: VideoLibraryCategory[];
  hasPermissionsToManageVideos: boolean;
  onEdit: (videoInfo: VideoLibraryItem) => void;
  onDelete: (videoInfo: VideoLibraryItem) => void;
}

const VideoCard = ({
  videoInfo,
  allCategories,
  thumbnailIndex,
  hasPermissionsToManageVideos,
  onEdit,
  onDelete,
}: Props) => {
  const { clientCode } = useClientContext();
  const navigate = useNavigate();

  const [isHovered, setIsHovered] = useState(false);
  const [isLinkJustCopied, setIsLinkJustCopied] = useState(false);

  const categories = videoInfo.categoryIds
    .map((categoryId) => allCategories.find((category) => category.id === categoryId))
    .filter(isDefined<VideoLibraryCategory>());

  const handlePlayClick = () => {
    navigate(`/${clientCode}/${pageRoutes.videoLibrary}/${videoInfo.id}`);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/${clientCode}/${pageRoutes.videoLibrary}/${videoInfo.id}`);
    setIsLinkJustCopied(true);
    setTimeout(() => setIsLinkJustCopied(false), 2000);
  };

  return (
    <Card
      sx={{ height: 344 }}
      variant="outlined"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box sx={{ position: "relative" }}>
        <CardMedia
          sx={{ height: 144 }}
          title="video thumbnail"
          image={thumbnailImages[thumbnailIndex % thumbnailImages.length]}
        />

        {videoInfo.duration > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              px: 0.75,
              borderRadius: "1rem",
              bgcolor: "rgba(0, 0, 0, 0.7)",
            }}
          >
            <Typography variant="caption" sx={{ color: "common.white" }}>
              {formatDurationInSeconds(videoInfo.duration)}
            </Typography>
          </Box>
        )}

        {isHovered && (
          <Box sx={{ position: "absolute", p: 0.5, top: 8, right: 8, bgcolor: "common.white", borderRadius: "4px" }}>
            <Tooltip arrow title={isLinkJustCopied ? "Copied" : "Copy link"}>
              <IconButton onClick={handleCopyLink}>{isLinkJustCopied ? <CheckIcon /> : <LinkIcon />}</IconButton>
            </Tooltip>

            {hasPermissionsToManageVideos && (
              <ActionsMenuButton
                items={[
                  {
                    label: "Edit Details",
                    icon: <EditIcon />,
                    onClick: () => onEdit(videoInfo),
                  },
                  {
                    label: "Delete",
                    icon: <DeleteIcon color="error" />,
                    onClick: () => onDelete(videoInfo),
                  },
                ]}
              />
            )}
          </Box>
        )}

        {isHovered && (
          <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1 }}>
            <Fab size="medium" onClick={handlePlayClick}>
              <PlayArrowIcon sx={{ fontSize: 32 }} />
            </Fab>
          </Box>
        )}
      </Box>
      <CardContent>
        <Typography variant="caption" color="textSecondary">
          {convertISODateTime(videoInfo.uploadedAt)}
        </Typography>
        <TypographyMultilineEllipsis maxLines={2} typographyProps={{ variant: "subtitle1", sx: { mb: 1.5, mt: 0.5 } }}>
          {videoInfo.name}
        </TypographyMultilineEllipsis>
        {videoInfo.description && (
          <TypographyMultilineEllipsis maxLines={2} typographyProps={{ color: "textSecondary", sx: { mb: 1.5 } }}>
            {videoInfo.description}
          </TypographyMultilineEllipsis>
        )}
        <InlineTags
          tags={categories.map((c) => ({ label: c.name, color: getCategoryColorFromNameHash(c.name) }))}
          maxDisplayedTags={2}
        />
      </CardContent>
    </Card>
  );
};

export default VideoCard;
